import axios from "axios";
import { toast } from "react-toastify";
import config from "../config.js";
import { parseError } from "../utils/utils.js";

// Create axios instance
const axiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get tokens from localStorage
const getTokens = () => ({
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken')
});

// Initialize tokens
// eslint-disable-next-line no-unused-vars
let { accessToken, refreshToken } = getTokens();

// Flag to track if a refresh is in progress
let isRefreshing = false;
// Store pending requests
let failedQueue = [];

// Process queue
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get fresh tokens for each request
    const { accessToken: currentAccessToken } = getTokens();
    if (currentAccessToken) {
      config.headers.Authorization = `Bearer ${currentAccessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh token for login endpoints - just reject the error
    if (originalRequest.url?.includes('/login/')) {
      return Promise.reject(error);
    }

    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // If refresh is in progress, add request to queue
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Get fresh refresh token
      const { refreshToken: currentRefreshToken } = getTokens();

      if (!currentRefreshToken) {
        throw new Error('No refresh token available');
      }

      // Attempt to refresh token
      const response = await axios.post(
        `${config.API_BASE_URL}/api/token/refresh/`,
        { refresh: currentRefreshToken }
      );

      const { access } = response.data;

      // Update tokens
      accessToken = access;
      localStorage.setItem('accessToken', access);

      // Update authorization header
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      originalRequest.headers.Authorization = `Bearer ${access}`;

      // Process queued requests
      processQueue(null, access);

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      // Clear tokens and redirect to login
      authService.clearTokens();
      window.location.href = '/login';

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth service methods
export const authService = {
  setTokens: (tokens) => {
    accessToken = tokens.access;
    refreshToken = tokens.refresh;
    localStorage.setItem('accessToken', tokens.access);
    localStorage.setItem('refreshToken', tokens.refresh);
  },

  clearTokens: () => {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  getAccessToken: () => localStorage.getItem('accessToken'),
  getRefreshToken: () => localStorage.getItem('refreshToken'),
};

// HTTP methods
export const httpClient = {
  get: async (url, config = {}) => {
    try {
      const response = await axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },

  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await axiosInstance.patch(url, data, config);
      return response.data;
    } catch (error) {
      handleError(error);
      throw error;
    }
  },
};


// Error handler
const handleError = (error) => {
  // Don't show automatic toast for login endpoints - let the component handle it
  if (error.config?.url?.includes('/login/')) {
    return;
  }

  // 404 on history endpoints means no history yet — not a user-visible error
  if (error.response?.status === 404 && error.config?.url?.includes('/history/')) {
    return;
  }

  toast.error(parseError(error));
};

// Password reset (no auth token needed)
export const forgotPassword = async (email) => {
  const response = await axiosInstance.post('/api/users/forgot-password/', { email });
  return response.data;
};

export const verifyOtp = async (email, otp) => {
  const response = await axiosInstance.post('/api/users/verify-otp/', { email, otp });
  return response.data;
};

export const resetPasswordWithOtp = async (email, otp, new_password, confirm_password) => {
  const response = await axiosInstance.post('/api/users/reset-password/', { email, otp, new_password, confirm_password });
  return response.data;
};

export const changePassword = async (data) => {
  const response = await axiosInstance.post('/api/users/settings/change-password/', data);
  return response.data;
};

export default httpClient;
